import { photo, responsivePhoto, type ResponsivePhoto } from "./media";

/**
 * Supabase-hosted photography for the services that have a real client photo,
 * keyed by slug.
 *
 * This is kept apart from `services-data.ts` because the build config imports
 * it to generate per-route hero preloads, and Vite loads its config in plain
 * Node — which cannot resolve the bundled image imports that the two
 * stock-photo services need. Everything here is a plain string.
 */

/**
 * One ladder serves both uses of these photos: the cards (~590px wide, so the
 * lower steps matter) and the full-bleed header on a service detail page.
 * Each `<img>` supplies its own `sizes`, so the browser picks correctly for
 * whichever context it is in.
 */
const SERVICE_WIDTHS = [480, 640, 768, 960, 1280, 1600];

/** Cards render at roughly a third of the viewport width, so quality can come
 *  down from the hero setting without any visible softening. */
const SERVICE_QUALITY = 74;

export const servicePhotos: Record<string, ResponsivePhoto> = {
  /** Freshly painted interior walls with floor masking still down. */
  "interior-painting": responsivePhoto(
    "1785824084094-589-IMG_6510.jpeg",
    SERVICE_WIDTHS,
    4 / 3,
    SERVICE_QUALITY,
  ),
  /** Stucco exterior masked and mid-repaint. */
  "exterior-painting": responsivePhoto(
    "1785823952100-477-IMG_8528.jpeg",
    SERVICE_WIDTHS,
    4 / 3,
    SERVICE_QUALITY,
  ),
  /** Home with solar screens fitted across every window. */
  "sun-screens": responsivePhoto(
    "1785823851441-704-IMG_8780.jpeg",
    SERVICE_WIDTHS,
    4 / 3,
    SERVICE_QUALITY,
  ),
  /** Finished patio cover / shade structure on a repainted home. */
  pergolas: responsivePhoto(
    "1785823916158-658-IMG_8716.jpeg",
    SERVICE_WIDTHS,
    4 / 3,
    SERVICE_QUALITY,
  ),
};

/**
 * Open Graph image per slug. Always a JPEG from the render endpoint — social
 * scrapers do not content-negotiate, so they get JPEG regardless.
 */
export const serviceOgPhotos: Record<string, string> = {
  "interior-painting": photo("1785824084094-589-IMG_6510.jpeg"),
  "exterior-painting": photo("1785823952100-477-IMG_8528.jpeg"),
  "sun-screens": photo("1785823851441-704-IMG_8780.jpeg"),
  pergolas: photo("1785823916158-658-IMG_8716.jpeg"),
};

/**
 * The two services with no client photo use licensed stock instead. Those
 * files are bundled (so the CDN fingerprints them), which is exactly what the
 * build config cannot load — hence the indirection: the names below are
 * resolved to hashed URLs by `stock-photos.ts` at runtime and by the
 * hero-preload plugin at build time.
 */
export const stockServiceFiles: Record<string, { widths: number[]; base: string }> = {
  "power-washing": { widths: [480, 640, 768, 960, 1280], base: "service-power-washing" },
  "rain-gutter-installation": { widths: [480, 640, 768, 960, 1280], base: "service-rain-gutters" },
};
